import { Component, Element, Fragment, h, Prop, State } from '@stencil/core';
import { baseUri, httpV2, language, translate } from '../../../utils/utils';
import { Shop } from '../../../models/shop.model';
import { Settings } from 'luxon';
import { customPortalState, shopIntegrationState, themeState } from '../../../utils/store';

enum ShopError {
  NONE = 'none',
  NOT_FOUND = 'not_found',
  UNKNOWN = 'unknown'
}

@Component({
  tag: 'ike-default-shops',
  styleUrl: 'ike-default-shops.scss',
  shadow: true,
})
export class IkeDefaultShops {
  @Element() el: HTMLIkeDefaultShopsElement;

  @Prop() groupUuid: string = '';
  @Prop() isPortal = false;
  @Prop() customPortalId = null;

  @State() shops: Shop[] = [];
  @State() isLoading = true;
  @State() error = ShopError.NONE;

  private currentPage = 1;
  private totalPages = 1;
  private observer: IntersectionObserver;

  componentWillLoad() {
    Settings.defaultLocale = language();
    if (this.customPortalId) {
      customPortalState.id = Number.parseInt(this.customPortalId);
    }
    this.loadDefaultShops();
    if (themeState.color === null) {
      themeState.color = '#FF3E60FF'
    }
  }

  private loadNextPage(): void {
    if (this.currentPage === this.totalPages) {
      this.isLoading = false;
      return;
    }

    this.currentPage++;

    this.loadDefaultShops();
  }

  private loadDefaultShops() {
    if (this.groupUuid === '') {
      this.error = ShopError.NOT_FOUND;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    httpV2('get', `/shop`, {
      with: [
        'language',
        'integration_link'
      ],
      type: 'default',
      filters: {
        group_uuids: [this.groupUuid],
        on_sale: true,
      },
      page: this.currentPage,
      order_for: {
        updated_at: 'desc'
      }
    })
    .then(response => {
      if (shopIntegrationState.isEmbedded && response.total === 1) {
        return document.dispatchEvent(
          new CustomEvent('updateShopCode', {
            detail: response.data[0].code
          })
        );
      }

      this.shops = [
        ...this.shops,
        ...response.data as Shop[]
      ];
      this.totalPages = response.pages;
    })
    .catch(error => {
      this.error = error.code === 404 ? ShopError.NOT_FOUND : ShopError.UNKNOWN;
    })
    .finally(() => {
      this.isLoading = false;
      requestAnimationFrame(() => {
        this.observeLastElement();  //In case we need to load more shops directly after the first rendering :)
      });
    })
  }

  private observeLastElement() {
    if (this.observer !== undefined) {
      this.observer.disconnect();
    }

    const lastShop = this.el.shadowRoot.querySelector('.shop-card:last-child');
    if (lastShop !== null) {
      this.observer = new IntersectionObserver(
        (entries) => {
          const lastEntry = entries[0];
          if (lastEntry.isIntersecting && !this.isLoading) {
            this.loadNextPage();
          }
        },
        {
          root: this.isPortal
            ? null
            : this.el.shadowRoot.querySelector('.container'),
          threshold: this.isPortal ? 0.2 : 0.1,
        }
      );

      this.observer.observe(lastShop);
    }
  }

  private handleShopClick(e, shop: Shop): void {
    e.preventDefault();
    document.dispatchEvent(
      new CustomEvent('updateShopCode', {
        detail: shop.code
      })
    );
  }

  disconnectedCallback() {
    if (this.observer !== undefined) {
      this.observer.disconnect();
    }
  }

  private renderShopCard(shop: Shop) {
    const imageUrl = shop.thumbnail_url ?? shop.poster_url ?? shop.background_url ?? 'https://infomaniak.events/metas/social.jpg';
    const hasEventCount = shop.event_count > 0;

    return (
      <a class="shop-card" href={this.isPortal ? shop.integration_link : ''}
         onClick={(e) => !this.isPortal && this.handleShopClick(e, shop)}>
        <div class="poster" style={{
          backgroundColor: shop.color,
        }}>
          <img src={imageUrl} alt="shop-poster" />
          {hasEventCount && <div class="event-count-pill">
            {shop.event_count} {translate('évènements')}
          </div>}
        </div>
        <div class="infos">
          <div class="title">
            {shop.language.title}
          </div>
        </div>
      </a>
    );
  }

  render() {
    return (
      <div class={{ 'container': true, 'is-modal': !this.isPortal && this.shops.length > 0, 'is-portal': this.isPortal && this.shops.length > 0 }}>
        {this.isLoading && this.shops.length === 0?
          <div class="loading-section">
            <ike-loader></ike-loader>
          </div>
        :
          (this.shops.length > 0 ?
            <Fragment>
              {this.shops.map((shop) => this.renderShopCard(shop))}
              {this.isLoading && <div class="loading-section loading-shop-card">
                <ike-loader></ike-loader>
              </div>}
            </Fragment>
          :
            <Fragment>
              <div class="no-default-shop">
                {translate("L'organisateur n'a pas de billetterie actuellement en ligne.")}
                {this.isPortal &&
                  <Fragment>
                    <a class="other-events-button" href={baseUri()}>
                      {translate("Voir d'autres événements")}
                    </a>
                    <div class="fake-cards">
                      <div class="fake-card"></div>
                      <div class="fake-card"></div>
                      <div class="fake-card"></div>
                      <div class="fake-card"></div>
                    </div>
                  </Fragment>}
              </div>
            </Fragment>
          )
        }
      </div>
    );
  }
}
